import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Star, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import AdminImageUploader from "../../components/AdminImageUploader";
import AdminPdfUploader from "../../components/AdminPdfUploader";

const empty = {
  name: "", category: "", short_description: "", description: "",
  specifications: "{}", images: [], image_url: "", pdf_url: "", featured: false,
};

export default function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const uploading = imgBusy || pdfBusy;

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      images: Array.isArray(p.images) ? p.images : [],
      specifications: JSON.stringify(p.specifications || {}, null, 2),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    let specs;
    try {
      specs = JSON.parse(form.specifications || "{}");
    } catch {
      toast.error("Specifications must be valid JSON");
      setBusy(false);
      return;
    }
    // Cover image_url tracks the first uploaded image so legacy consumers still work
    const cover = (form.images && form.images[0]) || form.image_url || "";
    const payload = { ...form, specifications: specs, image_url: cover };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Catalog</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Products</h1>
        </div>
        <Button data-testid="new-product-btn" onClick={openNew} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New Product
        </Button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3 font-semibold">Image</th>
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-left p-3 font-semibold">Category</th>
              <th className="text-left p-3 font-semibold">Featured</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const cover = (p.images && p.images[0]) || p.image_url;
              const coverUrl = cover && cover.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${cover}` : cover;
              return (
                <tr key={p.id} className="border-t border-slate-200">
                  <td className="p-3">
                    {coverUrl ? (
                      <img src={coverUrl} alt="" className="w-16 h-12 object-cover" />
                    ) : (
                      <div className="w-16 h-12 bg-slate-100 grid place-items-center text-[10px] text-slate-400">—</div>
                    )}
                  </td>
                  <td className="p-3 font-semibold">{p.name}</td>
                  <td className="p-3 text-slate-600">{p.category}</td>
                  <td className="p-3">{p.featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" /> : "—"}</td>
                  <td className="p-3 text-right space-x-2">
                    <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:bg-slate-100"><Edit className="w-4 h-4" /></button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => del(p)} className="p-2 hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-slate-500 text-sm">No products yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Product" : "New Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
                <Input data-testid="product-form-name" required value={form.name} onChange={setField("name")} className="rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Category *</Label>
                <Input data-testid="product-form-category" required value={form.category} onChange={setField("category")} className="rounded-none" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Short Description</Label>
              <Input value={form.short_description} onChange={setField("short_description")} className="rounded-none" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Full Description</Label>
              <Textarea rows={4} value={form.description} onChange={setField("description")} className="rounded-none" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <AdminImageUploader
                label="Product Images (up to 4)"
                value={form.images}
                onChange={(v) => setForm({ ...form, images: v })}
                max={4}
                testId="product-images"
                onBusy={setImgBusy}
              />
              <AdminPdfUploader
                label="Product Brochure (PDF)"
                value={form.pdf_url}
                onChange={(v) => setForm({ ...form, pdf_url: v })}
                testId="product-brochure"
                onBusy={setPdfBusy}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Specifications (JSON)</Label>
              <Textarea
                rows={6}
                value={form.specifications}
                onChange={setField("specifications")}
                className="rounded-none font-mono text-xs"
                placeholder='{"Voltage": "230V", "Power": "100W"}'
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={!!form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
              <Label>Featured on homepage</Label>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              {uploading && (
                <span className="text-xs text-amber-600 font-bold uppercase tracking-wider mr-auto inline-flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Uploading file…
                </span>
              )}
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button
                data-testid="product-form-submit"
                type="submit"
                disabled={busy || uploading}
                title={uploading ? "Wait for upload to finish" : ""}
                className="rounded-none btn-amber font-semibold disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editing ? "Save Changes" : "Create Product"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
